import { Injectable, ApplicationRef, inject } from '@angular/core';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { NavigationEnd, Router } from '@angular/router';
import { filter, first, concat, interval, catchError, of, firstValueFrom } from 'rxjs';
import { HttpClient } from '@angular/common/http';

interface VersionInfo {
  version: string;
  timestamp: string;
  buildDate: string;
}

@Injectable({
  providedIn: 'root'
})
export class AppUpdateService {
  private readonly swUpdate = inject(SwUpdate);
  private readonly appRef = inject(ApplicationRef);
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);

  private readonly CHECK_INTERVAL_MS = 30 * 60 * 1000; // 30 minutes
  private readonly RELOAD_COOLDOWN_MS = 60 * 1000; // 60s — guards against reload loops
  private readonly RELOAD_TS_KEY = 'sw_reload_ts';
  private currentVersion: string | null = '0.0.1-dev';
  private updateReady = false;
  private isApplyingUpdate = false;

  init() {
    if (!this.swUpdate.isEnabled) {
      return;
    }

    this.loadCurrentVersion();
    this.setupPeriodicUpdateCheck();
    this.listenForServiceWorkerUpdates();
    this.applyUpdateOnNavigation();
    this.applyUpdateOnVisibilityChange();
    this.checkPendingUpdate();
  }

  private loadCurrentVersion() {
    this.http.get<VersionInfo>('/version.json', {
      headers: { 'Cache-Control': 'no-cache' }
    })
      .pipe(catchError(() => of(null)))
      .subscribe(versionInfo => {
        if (versionInfo) {
          this.currentVersion = versionInfo.version;
          localStorage.setItem('app_version', this.currentVersion);
        }
      });
  }

  private setupPeriodicUpdateCheck() {
    const appIsStable$ = this.appRef.isStable.pipe(
      first(isStable => isStable === true)
    );
    const everyInterval$ = interval(this.CHECK_INTERVAL_MS);
    const everyIntervalOnceAppStable$ = concat(appIsStable$, everyInterval$);

    everyIntervalOnceAppStable$.subscribe(async () => {
      await this.checkForUpdate();
    });
  }
  

  private listenForServiceWorkerUpdates() {
    this.swUpdate.versionUpdates
      .pipe(filter((evt): evt is VersionReadyEvent => evt.type === 'VERSION_READY'))
      .subscribe(() => {
        this.updateReady = true;
      });

    this.swUpdate.unrecoverable.subscribe(() => {
      document.location.reload();
    });
  }

  /**
   * Apply update silently on next navigation — no confirmation dialog.
   * Navigation means the user already left the current page, so no data loss.
   */
  private applyUpdateOnNavigation() {
    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(() => {
        if (this.updateReady) {
          this.applyUpdate();
        }
      });
  }

  /**
   * Apply update when user returns to tab, but only if they were away
   * for at least 5 minutes (avoids reload during quick tab switches).
   */
  private applyUpdateOnVisibilityChange() {
    let hiddenSince: number | null = null;

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        hiddenSince = Date.now();
      } else if (document.visibilityState === 'visible' && this.updateReady) {
        const awayMs = hiddenSince ? Date.now() - hiddenSince : 0;
        hiddenSince = null;
        // Only reload if the tab was hidden for at least 5 minutes
        if (awayMs >= 5 * 60 * 1000) {
          this.applyUpdate();
        }
      }
    });
  }

  /**
   * Check if there was a pending update from last session
   */
  private checkPendingUpdate() {
    if (localStorage.getItem('pending_update') === 'true') {
      localStorage.removeItem('pending_update');
      this.applyUpdate();
    }
  }

  async checkForUpdate(): Promise<void> {
    try {
      const swUpdateAvailable = await this.swUpdate.checkForUpdate();
      if (swUpdateAvailable) {
        this.updateReady = true;
        return;
      }
      await this.checkVersionFile();
    } catch (error) {
      // silent
    }
  }

  private async checkVersionFile(): Promise<void> {
    try {
      const response = await firstValueFrom(
        this.http.get<VersionInfo>('/version.json', {
          headers: { 'Cache-Control': 'no-cache' }
        }).pipe(catchError(() => of(null)))
      );

      if (response && this.currentVersion && response.version !== this.currentVersion) {
        this.updateReady = true;
        localStorage.setItem('pending_update', 'true');
      }
    } catch (error) {
      // silent
    }
  }

  private async applyUpdate() {
    if (this.isApplyingUpdate) return;

    // Loop guard: if we've already reloaded recently in this tab session,
    // skip — prevents infinite reload if SW fails to activate the new bundle.
    const lastReload = Number(sessionStorage.getItem(this.RELOAD_TS_KEY) || 0);
    if (lastReload && Date.now() - lastReload < this.RELOAD_COOLDOWN_MS) {
      return;
    }

    this.isApplyingUpdate = true;
    sessionStorage.setItem(this.RELOAD_TS_KEY, String(Date.now()));

    try {
      if (this.swUpdate.isEnabled) {
        await this.swUpdate.activateUpdate();
      }
      document.location.reload();
    } catch (error) {
      this.isApplyingUpdate = false;
    }
  }

  async manualUpdateCheck(): Promise<void> {
    await this.checkForUpdate();
  }

  /**
   * Force an immediate update check and, if a new version is ready,
   * apply it and reload. Used at entry points (e.g. login) so users
   * who land on a stale cached bundle get the fresh one before any
   * API calls error out. Guarded by the cooldown in applyUpdate().
   */
  async checkAndApplyUpdate(): Promise<void> {
    if (!this.swUpdate.isEnabled) return;
    try {
      const available = await this.swUpdate.checkForUpdate();
      if (available) {
        this.updateReady = true;
        await this.applyUpdate();
      }
    } catch {
      // silent
    }
  }

  getCurrentVersion(): string | null {
    return this.currentVersion;
  }

  isUpdateAvailable(): boolean {
    return this.updateReady;
  }
}
